import React, { ReactNode, useCallback, useState } from 'react';
import {
    Box,
    CloseButton,
    Flex,
    useColorModeValue,
    Drawer,
    DrawerContent,
    DrawerOverlay,
    useDisclosure,
    BoxProps,
    Image,
} from '@chakra-ui/react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { MobileNav } from '@/components/Layout/MobileNav';
import { NavItem } from '@/components/Layout/NavItem';
import Logo from '@/images/jovelogo.svg';
import { useAuth } from '@/context/AuthContext';
import Icon from '../Icon';
import { getLinkItemsForRoles } from './utils/getLinkItemsForRoles';

/** Matches desktop sidebar width (Chakra space 60 ≈ 15rem) so drawer does not go full-screen. */
const SIDEBAR_WIDTH = '15rem';

// Cast via unknown so TS does not expand Chakra's polymorphic prop unions.
const LayoutBox = Box as unknown as React.FC<Record<string, unknown>>;
const LayoutDrawerContent = DrawerContent as unknown as React.FC<Record<string, unknown>>;
const LayoutImage = Image as unknown as React.FC<Record<string, unknown>>;
const LayoutFlex = Flex as unknown as React.FC<Record<string, unknown>>;
const LayoutCloseButton = CloseButton as unknown as React.FC<Record<string, unknown>>;

export const Layout = ({ children }: { children: ReactNode }) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [isNavPanelSmall, setIsNavPanelSmall] = useState(false);

    const handleToggleNavSize = useCallback(() => {
        setIsNavPanelSmall(!isNavPanelSmall);
    }, [isNavPanelSmall]);

    return (
        <LayoutBox minH="100vh" bg={useColorModeValue('gray.100', 'gray.900')}>
            <SidebarContent
                isNavPanelSmall={isNavPanelSmall}
                handleToggleNavSize={handleToggleNavSize}
                onClose={() => onClose}
                display={{ base: 'none', lg: 'block' }}
            />
            <Drawer
                autoFocus={false}
                isOpen={isOpen}
                placement="left"
                onClose={onClose}
                returnFocusOnClose={false}
                onOverlayClick={onClose}
                size="xs"
            >
                <DrawerOverlay />
                <LayoutDrawerContent maxW={SIDEBAR_WIDTH} w={SIDEBAR_WIDTH} bg="gray.900">
                    <SidebarContent onClose={onClose} />
                </LayoutDrawerContent>
            </Drawer>
            <MobileNav onOpen={onOpen} />
            <LayoutBox
                transition={'margin .3s ease-in-out'}
                ml={isNavPanelSmall ? { base: 0, lg: '70px' } : { base: 0, lg: 60 }}
                overflowX={'auto'}
            >
                <LayoutBox p={4} pt={0} mt={'1rem'}>
                    {children}
                </LayoutBox>
            </LayoutBox>
        </LayoutBox>
    );
};

interface SidebarProps extends BoxProps {
    onClose: () => void;
    handleToggleNavSize?: () => void;
    isNavPanelSmall?: boolean;
}

const SidebarContent = ({ onClose, handleToggleNavSize, isNavPanelSmall, ...rest }: SidebarProps) => {
    const { user } = useAuth();
    const { adminTranslations, adminBanner } = useFlags();

    const adminsLinkItems = getLinkItemsForRoles(user?.roles, { adminTranslations, adminBanner });
    const isDrawer = handleToggleNavSize == null;

    let sidebarWidth: string = SIDEBAR_WIDTH;
    if (isNavPanelSmall) {
        sidebarWidth = '71px';
    } else if (isDrawer) {
        sidebarWidth = '100%';
    }

    return (
        <LayoutBox
            borderRight="1px"
            bg={'gray.900'}
            borderRightColor={useColorModeValue('gray.200', 'gray.700')}
            w={sidebarWidth}
            maxW={SIDEBAR_WIDTH}
            transition={'width .3s ease-in-out'}
            pos={isDrawer ? 'relative' : 'fixed'}
            h="full"
            {...rest}
        >
            <LayoutBox
                h="100vh"
                overflowY="auto"
                maxW={SIDEBAR_WIDTH}
                minW="60px"
                css={{
                    '&::-webkit-scrollbar': {
                        width: '7px',
                    },
                    '&::-webkit-scrollbar-track': {
                        width: '7px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: '#fff',
                        borderRadius: '24px',
                    },
                }}
            >
                <LayoutFlex h="20" alignItems="center" mx="8" justifyContent="space-between">
                    {!isNavPanelSmall && <LayoutImage src={Logo} alt="jove logo" height={'44px'} />}
                    <LayoutCloseButton color={'#ffffff'} display={{ base: 'flex', lg: 'none' }} onClick={onClose} />
                </LayoutFlex>
                <LayoutFlex flexDirection="column" mb="60px">
                    {adminsLinkItems?.length
                        ? adminsLinkItems.map((link) => {
                              if (link.hide) return null;

                              return (
                                  <NavItem key={link.name} icon={link.icon} url={link.url}>
                                      {isNavPanelSmall ? '' : link.name}
                                  </NavItem>
                              );
                          })
                        : null}
                    {handleToggleNavSize ? (
                        <LayoutFlex
                            position="absolute"
                            bottom="2"
                            right="5"
                            padding="5px 10px"
                            _hover={{ cursor: 'pointer' }}
                            onClick={handleToggleNavSize}
                        >
                            <LayoutBox transform={isNavPanelSmall ? 'scale(-1)' : ''}>
                                <Icon color="white" icon="angle-left" fontSize="18px" />
                            </LayoutBox>
                        </LayoutFlex>
                    ) : null}
                </LayoutFlex>
            </LayoutBox>
        </LayoutBox>
    );
};
